import moment, { type Moment } from 'moment'
import { defineStore } from 'pinia'
import { i18n } from '../utils/i18n'
import rtcp from '../utils/rtcp'
import socket from '../utils/socket'
import { useModalStore } from './modal'
import { useUserStore } from './user'

interface State {
    inactiveTimeRemainingBeforeWarning: number
    inactiveTimeRemainingBeforeDisconnect: number
    formattedRemainingMinutes: string | null
    formattedRemainingSeconds: string | null
    startTimersAt: Moment | null
    inactivityWarningTimeout: number | null
    disconnectTimeout: number | null
    remainingTimeTimeout: number | null
}

export const useInactivityTimerStore = () => {
    const modalStore = useModalStore()
    const userStore = useUserStore()

    const store = defineStore('inactivityTimer', {
        state: (): State => ({
            inactiveTimeRemainingBeforeWarning: 15 * 60 * 1000, // 15 min
            inactiveTimeRemainingBeforeDisconnect: 5 * 60 * 1000, // 5 min
            formattedRemainingMinutes: null,
            formattedRemainingSeconds: null,
            startTimersAt: null,
            inactivityWarningTimeout: null,
            disconnectTimeout: null,
            remainingTimeTimeout: null,
        }),

        actions: {
            startTimers() {
                if (
                    !userStore.user.player_preferences
                        .is_auto_disconnect_enabled
                ) {
                    return
                }

                this.clearTimers()
                this.startTimersAt = moment()

                this.inactivityWarningTimeout = window.setTimeout(() => {
                    if (modalStore.is('disconnectedForInactivity')) {
                        return
                    }

                    modalStore.open('inactivityWarning')

                    this.updateRemainingTime()
                }, this.inactiveTimeRemainingBeforeWarning)

                this.disconnectTimeout = window.setTimeout(() => {
                    modalStore.open('disconnectedForInactivity')
                    socket.close()
                    const conn = rtcp.getConnection()
                    if (conn) {
                        conn.close()
                    }
                    this.clearTimers()
                }, this.inactiveTimeRemainingBeforeWarning + this.inactiveTimeRemainingBeforeDisconnect)
            },

            updateRemainingTime() {
                if (!this.startTimersAt) {
                    this.clearTimers()

                    return
                }

                const endTime = this.startTimersAt
                    .clone()
                    .add(
                        this.inactiveTimeRemainingBeforeDisconnect
                            + this.inactiveTimeRemainingBeforeWarning,
                        'milliseconds'
                    )
                const remainingTimeInMinutes = endTime.diff(moment(), 'minutes')
                const remainingTimeInSeconds =
                    endTime.diff(moment(), 'seconds') % 60

                this.remainingTimeTimeout = window.setTimeout(() => {
                    this.updateRemainingTime()
                }, 1000)

                this.formattedRemainingMinutes = i18n.t(
                    'common.sentence.inactivityRemainingMinutes',
                    {
                        count: remainingTimeInMinutes,
                    }
                )
                this.formattedRemainingSeconds = i18n.t(
                    'common.sentence.inactivityRemainingSeconds',
                    {
                        count: remainingTimeInSeconds,
                    }
                )
            },

            stopTimers() {
                this.clearTimers()
            },

            clearTimers() {
                if (this.inactivityWarningTimeout) {
                    clearTimeout(this.inactivityWarningTimeout)
                    this.inactivityWarningTimeout = null
                }

                if (this.disconnectTimeout) {
                    clearTimeout(this.disconnectTimeout)
                    this.disconnectTimeout = null
                }

                if (this.remainingTimeTimeout) {
                    clearTimeout(this.remainingTimeTimeout)
                    this.remainingTimeTimeout = null
                }
            },
        },
    })

    return store()
}
